import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { listTeams, createTeam, type Team, type Role } from "../api/teams.js";
import { humanError } from "../api/errors.js";
import { navigate } from "../nav.js";
import { toast, type BnBadgeVariant, type BnColumn, type BnRow } from "../components/index.js";

/** Maps a membership role to a badge variant so labels read consistently. */
function roleVariant(role: Role): BnBadgeVariant {
  switch (role) {
    case "owner":
      return "info";
    case "maintainer":
      return "success";
    default:
      return "neutral";
  }
}

/**
 * page-teams (/teams) lists the caller's teams with the caller's own role and
 * lets an owner create a new one. Rows link into <page-team> for the roster.
 */
@customElement("page-teams")
export class PageTeams extends LitElement {
  @state() private teams: Team[] = [];
  @state() private loading = true;
  @state() private error = "";

  @state() private showCreate = false;
  @state() private createName = "";
  @state() private creating = false;
  @state() private createError = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
    }
    .spacer {
      flex: 1;
    }
    .name-cell {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }
    .name-cell .display {
      font-weight: 600;
      color: var(--bino-text);
    }
    .name-cell .slug {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .role-cell {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.error = "";
    try {
      this.teams = await listTeams();
    } catch (err) {
      this.error = humanError(err);
    } finally {
      this.loading = false;
    }
  }

  private openCreate() {
    this.createName = "";
    this.createError = "";
    this.showCreate = true;
  }

  private async submitCreate() {
    const name = this.createName.trim();
    if (!name) {
      this.createError = "Enter a name for the team.";
      return;
    }
    this.creating = true;
    this.createError = "";
    try {
      const team = await createTeam(name);
      toast(`Team “${team.displayName || team.name}” created`, "success");
      this.showCreate = false;
      navigate(`/teams/${encodeURIComponent(team.id)}`);
    } catch (err) {
      this.createError = humanError(err);
    } finally {
      this.creating = false;
    }
  }

  private columns(): BnColumn[] {
    return [
      { key: "name", label: "Team", width: "50%" },
      { key: "role", label: "Your role" },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
  }

  private rows(): BnRow[] {
    return this.teams.map((t) => {
      const display = t.displayName || t.name;
      return {
      name: html`<div class="name-cell">
        <span class="display">${display}</span>
        ${display !== t.name ? html`<span class="slug">${t.name}</span>` : nothing}
      </div>`,
      role: html`<span class="role-cell">
        <bn-badge .variant=${roleVariant(t.role)}>${t.role}</bn-badge>
        ${t.personal ? html`<bn-badge variant="neutral">personal</bn-badge>` : nothing}
      </span>`,
      actions: html`<bn-button
        variant="secondary"
        size="sm"
        @click=${() => navigate(`/teams/${encodeURIComponent(t.id)}`)}
        >Manage</bn-button
      >`,
      };
    });
  }

  private renderBody() {
    if (this.loading) {
      return html`<bn-spinner block label="Loading teams…"></bn-spinner>`;
    }
    if (this.error) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load teams"
        message=${this.error}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => this.load()}>Retry</bn-button>
      </bn-empty-state>`;
    }
    if (this.teams.length === 0) {
      return html`<bn-empty-state
        icon="⣿"
        heading="No teams yet"
        message="Create a team to share workspaces with others."
      >
        <bn-button slot="actions" variant="primary" @click=${() => this.openCreate()}
          >New team</bn-button
        >
      </bn-empty-state>`;
    }
    return html`<bn-card flush>
      <bn-table .columns=${this.columns()} .rows=${this.rows()} empty="No teams yet"></bn-table>
    </bn-card>`;
  }

  override render() {
    return html`
      <div class="head">
        <div>
          <h1>Teams</h1>
        </div>
        <span class="spacer"></span>
        <bn-button variant="primary" @click=${() => this.openCreate()}>New team</bn-button>
      </div>

      ${this.renderBody()}

      <bn-modal
        ?open=${this.showCreate}
        heading="Create team"
        @close=${() => (this.showCreate = false)}
      >
        <bn-field
          label="Team name"
          ?required=${true}
          error=${this.createError}
          hint="A short name for the team. You’ll be its first owner."
        >
          <bn-input
            .value=${this.createName}
            placeholder="acme-analytics"
            ?invalid=${this.createError !== ""}
            @input=${(e: Event) => (this.createName = (e.target as HTMLInputElement).value)}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") void this.submitCreate();
            }}
          ></bn-input>
        </bn-field>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.showCreate = false)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${this.creating}
          @click=${() => this.submitCreate()}
          >Create team</bn-button
        >
      </bn-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-teams": PageTeams;
  }
}
